// Store for tracking single-select filter choices and selections
import { defineStore } from 'pinia'

export interface FilterOption {
  value: string
  text: string
}

interface Filter {
  // `key` should be a unique id that's going to be used as
  // the `value` of the `<select />` element. Ideally snake-cased
  // because it's used to talk to Rails backend.
  // Example:
  //   <select value="sector_group"> <------ key
  //     <option value="information_technology" />
  //   </select>
  // It's also the key that gets mapped to/from query params,
  // unless a `groupingKey` is specified.
  key: string
  // `name` is a unique, camel-cased reference used by
  // `<SelectFilter/> to control a given filter.
  // Example: <SelectFilter name="sectorGroup" />
  name: string
  // Array of options to be displayed inside the <select> element.
  options: FilterOption[]
  // Value of the selected option.
  selectedOption?: string
  // If provided, replaces `key` when mapping to/from query params.
  groupingKey?: string
  // dirty: boolean
  dirty?: boolean
}

interface FilterState {
  filters: Filter[]
}

// Generic filter store for single-select components.
// Should be used in conjunction with a component like `SelectFilter`.
// Basic usage - or you can see a working example in `RespondentsList.vue`:
//  In one component, define the filters you're interested in by calling
//  `registerFilter(filter: Filter)`. This adds the filter to the state.
//  In the same or in another component, declare a <SelectFilter/> with the
//  same name as the filter you registered.
//  Example:
//    1. registerFilter({ key: 'company', name: 'company', options: [] })
//    2. <SelectFilter name="company" />
//  Then at a later time, call `updateFilterOptions` to populate the select
//  with desired options.
export const useSelectFilterStore = defineStore('SelectFilterStore', {
  state: (): FilterState => ({
    filters: [],
  }),

  getters: {
    getFilter: (state) => {
      return (filterName: string) => {
        return state.filters.find((f) => f.name === filterName)
      }
    },
    getFilterOptions: (state) => {
      return (filterName: string) => {
        const filter = state.filters.find((f) => f.name === filterName)
        return filter?.options || []
      }
    },
    getSelectedOption: (state) => {
      return (filterName: string) => {
        const filter = state.filters.find((f) => f.name === filterName)
        return filter?.selectedOption
      }
    },
    // Returns an object where keys are the filter keys
    // and values are the selected options.
    asObject: (state) => {
      const filtersObj = state.filters.reduce((acc, filter) => {
        if (!filter.groupingKey) {
          acc[filter.key] = filter.selectedOption
        } else {
          if (!acc[filter.groupingKey]) acc[filter.groupingKey] = []
          if (filter.selectedOption) acc[filter.groupingKey].push(filter.selectedOption)
        }
        return acc
      }, {})

      // Joins grouped keys before returning
      Object.keys(filtersObj).forEach((key) => {
        if (Array.isArray(filtersObj[key])) {
          filtersObj[key] = filtersObj[key].length > 0 ? filtersObj[key].join(',') : null
        }
      })

      return filtersObj
    },
    selectedFiltersCount: (state) => {
      return state.filters.filter((filter) => filter.selectedOption).length
    },
  },

  actions: {
    isDirty: (name: string) => {
      return (this?.filters ?? []).find((f) => f.name === name)?.dirty || false
    },

    registerFilter(filter: Omit<Filter, 'selectedOption' | 'dirty'>) {
      const existingFilterIndex = this.filters.findIndex((f) => f.name === filter.name)

      if (existingFilterIndex !== -1) {
        // Update existing filter
        this.$patch({
          filters: this.filters.map((existingFilter, index) =>
            index === existingFilterIndex ? filter : existingFilter,
          ),
        })
      } else {
        // Add new filter
        this.$patch({
          filters: [...this.filters, filter],
        })
      }
    },
    setSelectedOption(filterName: string, value: any) {
      this.$patch({
        filters: this.filters.map((filter) =>
          filter.name === filterName ? { ...filter, selectedOption: value, dirty: true } : filter,
        ),
      })
    },
    clearAllFilters() {
      this.$patch({
        filters: this.filters.map((filter) => ({
          ...filter,
          selectedOption: null,
        })),
      })
    },
    updateFilterOptions(filterName: string, newOptions: FilterOption[]) {
      this.$patch({
        filters: this.filters.map((filter: Filter) =>
          filter.name === filterName
            ? {
                ...filter,
                options: newOptions,
              }
            : filter,
        ),
      })
    },
  },
})
